import moment from 'moment';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { accessTypes } from '../../helpers/Utils';
import { Colxx } from '../common/CustomBootstrap';
import InteractiveBarChart from './InteractiveBarChart';
import {
    formatPlacementLabel,
    listPlacements,
    PLACEMENT_COLORS,
} from './FeaturedSpDailyFetcher';

/**
 * Daily charts for featured SP performance (GA4-sourced). Reads the same
 * featuredSpDaily docs used by the stat cards and renders four bar charts:
 * impressions, clicks, CTR, and featured revenue.
 *
 * Mirrors DashboardCharts' prop contract so it can be dropped in right after
 * <DashboardCharts /> with the same date range and spId handling.
 */
const FeaturedCharts = ({
    dateRange,
    featuredDaily,
    previousFeaturedDaily,
    loading,
    error,
    filterBySpId = null,
}) => {
    const { user } = useSelector((state) => state.authUser);

    // Priority: explicit filterBySpId > SP user's own uid > null (admin aggregate)
    let spId = filterBySpId;
    if (!spId) {
        const role = user?.accessType || 'superAdmin';
        spId = !accessTypes.includes(role) ? user?.uid : null;
    }

    const dateRangeLabel = useMemo(() => {
        const daysCount = dateRange?.endDate && dateRange?.startDate
            ? moment(dateRange.endDate).diff(moment(dateRange.startDate), 'days') + 1
            : 7;
        return dateRange?.startDate && dateRange?.endDate
            ? `${moment(dateRange.startDate).format('MMM DD, YYYY')} - ${moment(dateRange.endDate).format('MMM DD, YYYY')}`
            : `Last ${daysCount} days`;
    }, [dateRange?.startDate, dateRange?.endDate]);

    const { currentPeriodRange, previousPeriodRange } = useMemo(() => {
        if (!dateRange?.startDate || !dateRange?.endDate) {
            return { currentPeriodRange: null, previousPeriodRange: null };
        }
        const currentStart = moment(dateRange.startDate);
        const currentEnd = moment(dateRange.endDate);
        const periodLength = currentEnd.diff(currentStart, 'days');
        const previousStart = currentStart.clone().subtract(periodLength + 1, 'days');
        const previousEnd = currentStart.clone().subtract(1, 'days');
        return {
            currentPeriodRange: `${currentStart.format('MMM DD, YYYY')} - ${currentEnd.format('MMM DD, YYYY')}`,
            previousPeriodRange: `${previousStart.format('MMM DD, YYYY')} - ${previousEnd.format('MMM DD, YYYY')}`,
        };
    }, [dateRange?.startDate, dateRange?.endDate]);

    // Per-day value extractor. For admin (no spId), pull from day.general; for
    // SP user / drilldown, pull from day.SP[spId].
    const pickDayValue = (day, adminField, spField) => {
        if (spId) {
            const s = day.SP?.[spId];
            return s ? (s[spField] || 0) : 0;
        }
        return day.general?.[adminField] || 0;
    };

    // Per-placement per-day series for the impressions/clicks charts. When
    // placement data exists for the range, we emit a stacked series keyed by
    // af_from. When it doesn't (e.g. days predating the af_from rollout that
    // haven't been re-backfilled), we fall back to a single 'impressions' /
    // 'clicks' key sourced from the day total so the chart still renders.
    // Defined before ctrData since ctrData and ctrDataKeys depend on it.
    const { placementDataKeys, impressionsByPlacement, clicksByPlacement, hasPlacementData } = useMemo(() => {
        const collectFromDays = (days) => {
            const acc = {};
            (days || []).forEach((day) => {
                const src = spId ? day.SP?.[spId]?.byPlacement : day.general?.byPlacement;
                if (!src) return;
                for (const [placement, vals] of Object.entries(src)) {
                    if (!acc[placement]) acc[placement] = { impressions: 0, clicks: 0 };
                    acc[placement].impressions += vals.impressions || 0;
                    acc[placement].clicks += vals.clicks || 0;
                }
            });
            return acc;
        };

        const currentTotals = collectFromDays(featuredDaily);
        const previousTotals = collectFromDays(previousFeaturedDaily);
        const placements = listPlacements(currentTotals, previousTotals);
        const placementsExist = placements.length > 0;

        const dataKeys = placements.map((placement) => ({
            key: placement,
            label: formatPlacementLabel(placement),
            color: PLACEMENT_COLORS[placement] || 'hsl(220, 9%, 60%)',
        }));

        const buildPlacementSeries = (metric) => (featuredDaily || []).map((day) => {
            const src = spId ? day.SP?.[spId]?.byPlacement : day.general?.byPlacement;
            const row = { date: day.date, timestamp: day.timestamp };
            placements.forEach((p) => {
                row[p] = src?.[p]?.[metric] || 0;
            });
            return row;
        });

        const buildTotalsSeries = (key, adminField, spField) => (featuredDaily || []).map((day) => ({
            date: day.date,
            timestamp: day.timestamp,
            [key]: pickDayValue(day, adminField, spField),
        }));

        return {
            placementDataKeys: dataKeys,
            impressionsByPlacement: placementsExist
                ? buildPlacementSeries('impressions')
                : buildTotalsSeries('impressions', 'totalImpressions', 'impressions'),
            clicksByPlacement: placementsExist
                ? buildPlacementSeries('clicks')
                : buildTotalsSeries('clicks', 'totalClicks', 'clicks'),
            hasPlacementData: placementsExist,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featuredDaily, previousFeaturedDaily, spId]);

    // CTR per day: ratio stored in the doc (avgCtr at general level, ctr at
    // SP level) expressed as a 0–100 percentage. When per-placement data
    // exists, additional series are emitted per placement so the chart can
    // switch between Overall / Provider List / Banners. Stacking would be
    // misleading here (rates don't sum) so the CTR chart stays unstacked.
    const ctrData = useMemo(() => {
        if (!featuredDaily || featuredDaily.length === 0) return [];
        return featuredDaily.map((day) => {
            const src = spId ? day.SP?.[spId] : day.general;
            const overall = (spId ? src?.ctr : src?.avgCtr) || 0;
            const row = {
                date: day.date,
                timestamp: day.timestamp,
                ctr: Number((overall * 100).toFixed(2)),
            };
            for (const placement of placementDataKeys.map((d) => d.key)) {
                const placementCtr = src?.byPlacement?.[placement]?.ctr || 0;
                row[placement] = Number((placementCtr * 100).toFixed(2));
            }
            return row;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featuredDaily, spId, placementDataKeys]);

    const ctrDataKeys = useMemo(() => {
        const overall = { key: 'ctr', label: 'Overall', color: 'hsl(38, 92%, 50%)' };
        if (!hasPlacementData) return [overall];
        return [overall, ...placementDataKeys];
    }, [hasPlacementData, placementDataKeys]);

    // Bounce rate per day: clicks that didn't convert to a completed order,
    // (clicks - orders) / clicks. Single-series — orders aren't tagged with
    // af_from in the cloud function so we don't break this down by placement.
    const bounceRateData = useMemo(() => {
        if (!featuredDaily || featuredDaily.length === 0) return [];
        return featuredDaily.map((day) => {
            const clicks = pickDayValue(day, 'totalClicks', 'clicks');
            const orders = pickDayValue(day, 'totalOrders', 'orders');
            const bounce = clicks > 0 ? ((clicks - orders) / clicks) * 100 : 0;
            return {
                date: day.date,
                timestamp: day.timestamp,
                bounceRate: Number(bounce.toFixed(2)),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featuredDaily, spId]);

    const revenueData = useMemo(() => {
        if (!featuredDaily || featuredDaily.length === 0) return [];
        return featuredDaily.map((day) => ({
            date: day.date,
            revenue: pickDayValue(day, 'totalRevenue', 'revenue'),
            timestamp: day.timestamp,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featuredDaily, spId]);

    // Trend comparisons vs previous period. CTR trend compares the aggregated
    // clicks/impressions ratio so heavy days dominate correctly (not an
    // unweighted mean of daily rates).
    const trends = useMemo(() => {
        const sumField = (days, adminField, spField) => {
            let total = 0;
            (days || []).forEach((day) => {
                if (spId) {
                    total += day.SP?.[spId]?.[spField] || 0;
                } else {
                    total += day.general?.[adminField] || 0;
                }
            });
            return total;
        };

        const buildTrend = (current, previous) => {
            const percentage = previous > 0
                ? ((current - previous) / previous) * 100
                : (current > 0 ? 100 : null);
            return {
                percentage,
                currentTotal: current,
                previousTotal: previous || 0,
                currentPeriod: currentPeriodRange,
                previousPeriod: previousPeriodRange,
            };
        };

        const curImpr = sumField(featuredDaily, 'totalImpressions', 'impressions');
        const prevImpr = sumField(previousFeaturedDaily, 'totalImpressions', 'impressions');
        const curClicks = sumField(featuredDaily, 'totalClicks', 'clicks');
        const prevClicks = sumField(previousFeaturedDaily, 'totalClicks', 'clicks');
        const curOrders = sumField(featuredDaily, 'totalOrders', 'orders');
        const prevOrders = sumField(previousFeaturedDaily, 'totalOrders', 'orders');
        const curRev = sumField(featuredDaily, 'totalRevenue', 'revenue');
        const prevRev = sumField(previousFeaturedDaily, 'totalRevenue', 'revenue');

        const curCtr = curImpr > 0 ? (curClicks / curImpr) * 100 : 0;
        const prevCtr = prevImpr > 0 ? (prevClicks / prevImpr) * 100 : 0;
        const curBounce = curClicks > 0 ? ((curClicks - curOrders) / curClicks) * 100 : 0;
        const prevBounce = prevClicks > 0 ? ((prevClicks - prevOrders) / prevClicks) * 100 : 0;

        return {
            impressionsTrend: buildTrend(curImpr, prevImpr),
            clicksTrend: buildTrend(curClicks, prevClicks),
            ctrTrend: buildTrend(Number(curCtr.toFixed(2)), Number(prevCtr.toFixed(2))),
            bounceRateTrend: buildTrend(Number(curBounce.toFixed(2)), Number(prevBounce.toFixed(2))),
            revenueTrend: buildTrend(curRev, prevRev),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featuredDaily, previousFeaturedDaily, spId, currentPeriodRange, previousPeriodRange]);

    if (loading) {
        return <div className="loading" />;
    }

    return (
        <>
            {error && (
                <div className="text-center text-muted p-4" style={{ marginBottom: '1rem' }}>
                    Error loading featured analytics: {error}
                </div>
            )}
            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Featured Impressions"
                    description={dateRangeLabel}
                    data={impressionsByPlacement}
                    dataKeys={hasPlacementData ? placementDataKeys : [
                        { key: 'impressions', label: 'Impressions', color: 'hsl(262, 83%, 58%)' },
                    ]}
                    height={300}
                    stacked={hasPlacementData}
                    trendData={trends.impressionsTrend}
                />
            </Colxx>

            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Featured Clicks"
                    description={dateRangeLabel}
                    data={clicksByPlacement}
                    dataKeys={hasPlacementData ? placementDataKeys : [
                        { key: 'clicks', label: 'Clicks', color: 'hsl(330, 81%, 60%)' },
                    ]}
                    height={300}
                    stacked={hasPlacementData}
                    trendData={trends.clicksTrend}
                />
            </Colxx>
            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Featured CTR (%)"
                    description={dateRangeLabel}
                    data={ctrData}
                    dataKeys={ctrDataKeys}
                    height={300}
                    grouped={hasPlacementData}
                    trendData={trends.ctrTrend}
                />
            </Colxx>

            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Featured Bounce Rate (%)"
                    description={dateRangeLabel}
                    data={bounceRateData}
                    dataKeys={[
                        {
                            key: 'bounceRate',
                            label: 'Bounce Rate',
                            color: 'hsl(0, 84%, 60%)', // red
                        },
                    ]}
                    height={300}
                    trendData={trends.bounceRateTrend}
                    invertTrend={true}
                />
            </Colxx>

            <Colxx xxs="12" lg="6" className="mb-4">
                <InteractiveBarChart
                    title="Daily Featured Revenue"
                    description={dateRangeLabel}
                    data={revenueData}
                    dataKeys={[
                        {
                            key: 'revenue',
                            label: 'Featured Revenue',
                            color: 'hsl(172, 66%, 45%)', // teal
                        },
                    ]}
                    height={300}
                    trendData={trends.revenueTrend}
                    isCurrency={true}
                />
            </Colxx>


        </>
    );
};

export default React.memo(FeaturedCharts, (prevProps, nextProps) => {
    return (
        prevProps.dateRange?.startTimestamp === nextProps.dateRange?.startTimestamp &&
        prevProps.dateRange?.endTimestamp === nextProps.dateRange?.endTimestamp &&
        prevProps.loading === nextProps.loading &&
        prevProps.error === nextProps.error &&
        prevProps.featuredDaily === nextProps.featuredDaily &&
        prevProps.previousFeaturedDaily === nextProps.previousFeaturedDaily &&
        prevProps.filterBySpId === nextProps.filterBySpId
    );
});
